import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isHttpSecure } from "lib/utils/utils";
import { useARCConnection } from "./useARCConnection";
import { ARCContext } from "./ARCContext";
import { ConnectionState } from "lib/consts/ARC/connection";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import { ARC_GATEWAY, arcPathPrefix, isDirectArcMode } from "lib/consts/arcGateway";

// Control backend for the single-ARC control path, env-driven so it can be toggled
// without code edits. Defaults to rosbridge ("ROSLIBJS"); set to "FOXGLOVE" to use
// foxglove_bridge. Applies to both fleet-connect and direct-connection modes.
const CONTROL_SOURCE = process.env.REACT_APP_CONTROL_SOURCE || "ROSLIBJS";

/**
 * SingleARCProvider - Simplified ARC provider for direct connection mode
 * Connects to a hardcoded ARC at 172.30.1.24 without fleet dependencies
 *
 * Provides same context interface as ARCProvider for ControlLayout compatibility
 *
 * TODO: Add hook to query ARC itself for serial number dynamically
 */
const SingleARCProvider = ({ children, fleetConnectInfo: fleetConnectInfoProp }) => {
    const { state } = useLocation();
    const fleetConnectInfo = fleetConnectInfoProp ?? state?.fleetConnectInfo;

    const selectedARC = useMemo(() => {
        if (fleetConnectInfo) {
            const { arcId, proxyPublicHostname } = fleetConnectInfo;
            return {
                serialNumber: arcId,
                payload: "default",
                host: proxyPublicHostname,
                isSecure: true,
                controlSource: CONTROL_SOURCE,
                controlSourceOptions: {
                    useSSL: true,
                    port: null,
                    subpath: `${arcPathPrefix(arcId)}${ARC_GATEWAY.ros}`.replace(/^\//, ""),
                    fleetArcId: arcId,
                },
                robot: { serialNumber: arcId, name: arcId, type: "spot" },
            };
        }
        // Direct connection (ARC mode without fleet)
        const directIsSecure = isHttpSecure();
        // Mark direct mode EXPLICITLY rather than inferring it from a hostname
        // string further down the stack. When arc_gateway is in front, ROS is
        // reached same-origin at /api/ros with no port; otherwise we fall back
        // to the legacy per-port URL (dev against a bare device).
        const useGateway = isDirectArcMode();
        return {
            serialNumber: "ARC-1100",
            payload: "default",
            host: process.env.REACT_APP_SINGLE_ARC_HOSTNAME || window.location.hostname,
            isSecure: directIsSecure,
            controlSource: CONTROL_SOURCE,
            controlSourceOptions: {
                useSSL: directIsSecure,
                // `port: null` omits the port entirely — same origin, default
                // port. Anything else would point at a listener the browser
                // must not use once the gateway owns the origin.
                ...(useGateway
                    ? { port: null, subpath: ARC_GATEWAY.ros.replace(/^\//, "") }
                    : {}),
            },
            robot: {
                serialNumber: "ROBOT-1100",
                // Display name prefers jetson_system_status hostname (TopBar);
                // host is a temporary fallback until /status responds.
                name: process.env.REACT_APP_SINGLE_ARC_HOSTNAME || window.location.hostname,
                type: "spot",
            },
        };
    }, [fleetConnectInfo]);

    const { controlSocket, connectionState, sendCommand, callService } = useARCConnection(selectedARC);

    // Arc status state (battery level, online status, etc.)
    const [arcStatus, setArcStatus] = useState({
        online: false,
        batteryLevel: undefined,
        timestamp: undefined,
    });

    // Subscribe to ROS2 battery level topic
    useEffect(() => {
        if (controlSocket && connectionState === ConnectionState.Connected) {
            const handleBatteryUpdate = (data) => {
                setArcStatus((prev) => ({
                    ...prev,
                    batteryLevel: data, // Float32 unwrapped to number
                    timestamp: Date.now(),
                    online: true,
                }));
            };

            const cleanup = attachRosTopicWithRetry(
                controlSocket,
                "/arc/state/battery_level",
                handleBatteryUpdate,
                {
                    ...LATE_ROS_TOPIC_OPTIONS,
                    markMessageReceived: (data) => typeof data === "number" && !Number.isNaN(data),
                }
            );

            return cleanup;
        } else {
            // Mark offline when disconnected
            setArcStatus((prev) => ({ ...prev, online: false }));
        }
    }, [controlSocket, connectionState]);

    // Provide same context shape as ARCProvider for component compatibility
    // selectARC is a no-op since we only have one hardcoded ARC
    const selectARC = () => {
        console.warn("selectARC is not supported in single ARC mode");
    };

    return (
        <ARCContext.Provider
            value={{
                controlSocket,
                connectionState,
                sendCommand,
                callService,
                selectedARC,
                selectARC,
                selectedARCSerial: selectedARC.serialNumber,
                arcStatus,
            }}
        >
            {children}
        </ARCContext.Provider>
    );
};

export default SingleARCProvider;
